import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PhoneOff } from 'lucide-react';
import { Button, EmptyState, Skeleton, toast } from '@cyphex/ui';
import { ROUTES } from '@/constants/routes';
import { useStoreValue } from '@/lib/tinyStore';
import { useCallControls } from '../hooks/useCallControls';
import { useCallGrant } from '../hooks/useCallGrant';
import { useCallLifecycle } from '../hooks/useCallLifecycle';
import { useCallSurfaceExit } from '../hooks/useCallSurfaceExit';
import { usePlaceCall } from '../hooks/usePlaceCall';
import { activeCallActions, activeCallStore, selectHandoff } from '../store/activeCallStore';
import { pendingTransferStore, selectPendingTransfer } from '../store/pendingTransferStore';
import { CallRoom } from './room/CallRoom';
import { CallEndedScreen, CallRingingScreen } from './room/CallOutcomeScreens';
import { ConsultTransferBar } from './room/ConsultTransferBar';
import { VoicemailComposer } from './VoicemailComposer';
import { liveCallActions } from '../store/liveCallStore';
import { shouldDropCompactTile } from '../lib/callTile';
import { CallPipBar } from './CallPipBar';
import { useCallActions } from '../hooks/useCallActions';
import { useCallWindowLifecycle } from '../hooks/useCallWindowLifecycle';
import { useLeaveVoicemail, useVoicemailEligibility } from '../hooks/useLeaveVoicemail';

export interface CallSurfaceProps {
  callId: string;
  /**
   * The user is somewhere else in the app. The media surface stays MOUNTED —
   * hiding it rather than unmounting it is what keeps the call up — and a
   * corner tile stands in for it.
   */
  compact: boolean;
}

/**
 * The media surface for a CALL.
 *
 * Mounted by `ActiveCallHost` above the router rather than by a route, so that
 * walking away from `/call/:id` shrinks the call instead of ending it. The
 * grant arrives either in the handoff store (just placed or answered) or from
 * `POST /calls/:id/join` (reload, pasted link, or a call this tab rejoined).
 */
export function CallSurface({ callId, compact }: CallSurfaceProps) {
  const navigate = useNavigate();
  const exit = useCallSurfaceExit();
  const grant = useCallGrant(callId);
  const lifecycle = useCallLifecycle(callId);
  const handoff = useStoreValue(activeCallStore, selectHandoff);
  const [localEnd, setLocalEnd] = useState<string | null>(null);
  const onEnded = useCallback(() => setLocalEnd('Call ended'), []);
  const controls = useCallControls(callId, onEnded);
  const placeCall = usePlaceCall();
  const actions = useCallActions(callId);

  /* An attended transfer I started puts me on the CONSULT leg while the call I
     am handing over waits. Its finish/abandon controls belong here, on the leg
     I am standing in, and act on the original call's id. */
  const pending = useStoreValue(pendingTransferStore, selectPendingTransfer);
  const consulting = pending !== null && pending.consultCallId === callId ? pending : null;

  const peerId = handoff !== null && handoff.callId === callId ? handoff.peerId : null;
  const endedReason = localEnd ?? lifecycle.endedReason;
  const ringing = !grant.startsConnected && !lifecycle.connected;

  /* THE SERVER DECIDES whether a message may be left — the callee may take no
     voicemail, the call may have been answered after all, or a message may
     already be on it. Offering the control and failing on submit is exactly the
     hollow surface this replaces. */
  const eligibility = useVoicemailEligibility(callId, endedReason !== null);
  const voicemail = useLeaveVoicemail(callId);

  /* Leaving by any route releases the handoff, so a stale grant can never be
     replayed into a call that has since ended. */
  useEffect(() => () => activeCallActions.clear(callId), [callId]);

  /* Leaving drops the call from the live store FIRST, so the corner tile goes
     with it. Clearing only on navigation would leave a tile for a call that has
     already ended sitting in the corner of every other page. */
  /*
   * A FINISHED CALL RETURNS YOU TO THE CONVERSATION, not to a log of calls.
   *
   * This used to land on `/calls`, which is a HISTORY — a list of things that
   * already happened. Almost nobody wants that after hanging up; they want the
   * thread with the person they were just speaking to, which is where anything
   * they agreed on gets written down. Chat is also the app's home now, so this
   * agrees with everything else.
   */
  const leaveCall = useCallback(() => {
    liveCallActions.leave(callId);
    /* IN A DESKTOP CALL WINDOW THERE IS NOWHERE TO NAVIGATE TO. This window is
       the call and nothing else; sending it to `/chat` would turn it into a
       second workspace with its own sidebar, its own chat list and its own
       everything — which is precisely the duplicate the whole arrangement
       exists to avoid. Closing it is the exit.

       That rule lives in `useCallSurfaceExit` now rather than here, because
       three OTHER ways out of a call — blind transfer, completing an attended
       one, escalating to a meeting — each navigated a call window into a second
       workspace of its own while this one alone was guarded. */
    exit.leave(ROUTES.chat);
  }, [exit, callId]);

  /* The window and the call, tied together in both directions: the OS close
     button hangs up first, and a call that has ended does not leave a window
     sitting in the taskbar. No-op in a browser. */
  useCallWindowLifecycle({
    callId,
    ended: endedReason !== null,
    /* Neither a merge nor an escalation is an ending with a screen: the people
       on this call have been moved into the surviving call, or into the meeting
       it became, and the WORKSPACE is opening that right now. This window is
       left holding a call that no longer exists, and nothing else would ever
       tell it so. */
    movedAway: lifecycle.merged || lifecycle.escalated,
    canLeaveVoicemail: eligibility?.canLeave === true,
    hangup: controls.hangup,
  });

  /*
   * A call that ends while you are reading something else should not paint an
   * end screen into the corner of the app. Teams just takes the tile away, and
   * the call is in your history either way. The rule — including why an
   * ESCALATION counts as an ending here and nowhere else — is
   * `shouldDropCompactTile`, where it can be held by a test.
   */
  useEffect(() => {
    if (shouldDropCompactTile({ compact, endedReason, escalated: lifecycle.escalated })) {
      liveCallActions.leave(callId);
    }
  }, [compact, endedReason, lifecycle.escalated, callId]);

  /*
   * A CALL THAT IS OVER IS NOT AN ERROR STATE TO SIT IN.
   *
   * `/call/:id` mints its grant from `POST /calls/:id/join`, which fails once
   * the call has ended — and landing here with a dead call is ORDINARY, not
   * exceptional: a reload after somebody hung up, the back button, a link from
   * a notification, or answering a ring that had already timed out (45s by
   * default, enforced server-side, which ends the call `missed`).
   *
   * Parking on a full-page "This call is no longer available" makes a normal
   * ending look like a fault, and leaves the user on a dead-end screen whose
   * only exit is a button. Teams drops you back where you were. So do we: one
   * toast that says what happened, and back to the conversation.
   */
  useEffect(() => {
    if (compact) return;
    if (!grant.isError && grant.credentials !== null) return;
    if (grant.isLoading) return;
    toast.info('That call has ended.');
    leaveCall();
  }, [compact, grant.isError, grant.isLoading, grant.credentials, leaveCall]);

  if (endedReason !== null) {
    if (compact) return null;
    return (
      <CallEndedScreen
        peerName={grant.peerName}
        reason={endedReason}
        durationMs={null}
        onCallAgain={() =>
          peerId === null
            ? leaveCall()
            : placeCall.place({ calleeIds: [peerId], kind: grant.kind, peerName: grant.peerName })
        }
        onBack={leaveCall}
        voicemail={
          eligibility?.canLeave === true && eligibility.recipient !== null ? (
            <VoicemailComposer
              recipientName={eligibility.recipient.displayName}
              isSending={voicemail.isSending}
              isSent={voicemail.isSent}
              onSend={voicemail.send}
            />
          ) : null
        }
      />
    );
  }

  if (grant.isLoading) {
    if (compact) return null;
    return (
      <div className="flex h-full flex-col gap-2 bg-stage p-4">
        <Skeleton className="h-12 w-full" />
        <Skeleton shape="card" className="flex-1" />
      </div>
    );
  }


  if (grant.isError || grant.credentials === null) {
    if (compact) return null;
    return (
      <div className="flex h-full items-center justify-center bg-background p-8">
        <EmptyState
          icon={PhoneOff}
          tone="error"
          title="This call is no longer available"
          description="It may have ended, or you may not have been part of it."
          action={<Button variant="primary" onClick={leaveCall}>Back to chat</Button>}
        />
      </div>
    );
  }

  return (
    <>
      <div className={compact ? 'hidden' : 'h-full'}>
        {/* The room is mounted THROUGHOUT the ring: the caller publishes into an
            empty room from the first second, so when the callee's tracks arrive
            there is no renegotiation — only the ringing overlay going away. */}
        <CallRoom
          callId={callId}
          credentials={grant.credentials}
          peerName={grant.peerName}
          withVideo={grant.withVideo}
          compact={compact}
          onHold={controls.onHold}
          onToggleHold={controls.toggleHold}
          onHangup={controls.hangup}
          /*
           * THE RING COVERS THE PICTURE, NOT THE CHROME.
           *
           * This was an `absolute inset-0 z-10` sibling of the whole room, and
           * `ringing` is true for the ENTIRE outgoing call — so an opaque,
           * full-height screen sat over the header bar that now carries the
           * call toolbar. Chat could not be clicked, tabbed to, or even seen
           * for the one stretch of a call where writing to the person you are
           * ringing is most useful. Handing the screen to the room as a STAGE
           * overlay puts it over the video and under nothing else.
           *
           * IT CANNOT REMOUNT THE ROOM. `CallRoom` is rendered here
           * unconditionally either way — same component, same position, no
           * key — so React reconciles it in place and `useCallRoom`'s media
           * session, which has been publishing into the empty SFU room since
           * the first second, is never torn down. What mounts and unmounts is
           * a leaf INSIDE the stage wrapper, a sibling of `CallStage` rather
           * than an ancestor of it, so not even the <video> elements are
           * re-parented. Answering still costs no renegotiation.
           */
          stageOverlay={
            ringing ? (
              <CallRingingScreen peerName={grant.peerName} onCancel={controls.hangup} />
            ) : null
          }
          banner={
            consulting === null ? null : (
              <ConsultTransferBar
                transfer={consulting}
                busy={actions.isBusy}
                /* THIS call survives the merge — I am standing in it — and the
                   person being handed over is moved in here, so nobody has to
                   navigate anywhere. */
                onMerge={() => actions.merge(consulting.originalCallId)}
              />
            )
          }
        />
      </div>

      {compact ? (
        <CallPipBar
          peerName={grant.peerName}
          ringing={ringing}
          onReturn={() => navigate(ROUTES.call(callId))}
          onHangup={controls.hangup}
        />
      ) : null}
    </>
  );
}
